import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Checkbox,
    IconButton,
    Stack,
    Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FoodsIcon, FoodsTags } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import {
    type ConsentGovernanceItem,
    type ConsentGovernanceVersion,
} from "src/api/client-api/consent/useGetConsentGovernanceAdmin";
import { ConsentControlledSwitch } from "../ConsentControlledSwitch";
import { CONSENT_TYPE_ICON_CONFIG, getConsentCategoryConfig, hasKnownConsentCategory } from "../consentCategoryConfig";
import {
    formatConsentVersionBadgeLabel,
    formatReadableDate,
    getCatalogAvailabilityDescriptor,
    getConsentVersionTitle,
} from "../utils/consentGovernanceFormatters";
import { ConsentActionIconButton } from "./ConsentAdminShared";

type ConsentItemRowProps = {
    highlighted?: boolean;
    initiallyExpanded?: boolean;
    isPatchPending: boolean;
    item: ConsentGovernanceItem;
    latestVersion?: ConsentGovernanceVersion;
    onDelete: (item: ConsentGovernanceItem) => void;
    onEdit: (item: ConsentGovernanceItem) => void;
    onPreview: (item: ConsentGovernanceItem, version?: ConsentGovernanceVersion) => void;
    onSelect: (itemId: number, selected: boolean) => void;
    onToggleActive: (item: ConsentGovernanceItem, active: boolean) => void;
    selected: boolean;
};

export const ConsentItemRow = ({
    highlighted = false,
    initiallyExpanded = false,
    isPatchPending,
    item,
    latestVersion,
    onDelete,
    onEdit,
    onPreview,
    onSelect,
    onToggleActive,
    selected,
}: ConsentItemRowProps) => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const uiLocale = i18n.resolvedLanguage || i18n.language || "en";
    const availabilityDescriptor = getCatalogAvailabilityDescriptor(item.active);
    const accentColor =
        item.type === "document" ?
            theme.palette.error.main
        :   (theme.palette.primary[600] ?? theme.palette.primary.dark);
    const hasCategory = Boolean(item.category?.trim());
    const categoryConf = hasCategory ? getConsentCategoryConfig(item.category) : null;
    const categoryLabel =
        categoryConf ?
            hasKnownConsentCategory(item.category) ? t(categoryConf.labelKey, categoryConf.labelFallback)
            :   (item.category?.trim() ?? null)
        :   null;
    const categoryColor =
        categoryConf ?
            categoryConf.colorKey === "grey" ?
                theme.custom.grey[2000]
            :   theme.palette[categoryConf.colorKey].main
        :   undefined;
    const categoryTagVariant =
        categoryConf?.colorKey === "error" ? "tags-red"
        : categoryConf?.colorKey === "success" ? "tags-green"
        : categoryConf?.colorKey === "warning" ? "tags-coral"
        : categoryConf?.colorKey === "info" || categoryConf?.colorKey === "primary" ? "tags-blue"
        : "tags-grey";
    const itemIconColor = categoryColor ?? accentColor;
    const itemIcon = categoryConf?.icon ?? CONSENT_TYPE_ICON_CONFIG[item.type].icon;
    const editLabel = t("common.actions.edit", "Edit");
    const previewLabel = t("settings.consent.preview.action", "Preview");
    const deleteLabel = t("common.actions.delete", "Delete");
    const previewDisabledLabel = t("settings.consent.preview.no_version", "No version is available for preview.");
    const latestVersionDate = latestVersion?.publishedAt ?? latestVersion?.createdAt ?? null;
    const latestVersionTitle = getConsentVersionTitle(latestVersion);
    const latestVersionDateLabel =
        latestVersionDate ?
            latestVersion?.publishedAt ?
                t("settings.consent.version.published_on", "Published on {{date}}", {
                    date: formatReadableDate(latestVersionDate, uiLocale),
                })
            :   t("settings.consent.version.created_on", "Created on {{date}}", {
                    date: formatReadableDate(latestVersionDate, uiLocale),
                })
        :   null;

    return (
        <Accordion
            defaultExpanded={initiallyExpanded}
            disableGutters
            elevation={0}
            sx={{
                border: `1px solid ${highlighted ? theme.palette.warning.main : theme.custom.grey[500]}`,
                borderRadius: `${theme.spacing(1.5)} !important`,
                overflow: "hidden",
                boxShadow: highlighted ? `0 0 0 3px ${alpha(theme.palette.warning.main, 0.12)}` : "none",
                transition: "border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease",
                fontFamily: theme.typography.fontFamily,
                "& .MuiTypography-root": {
                    fontFamily: theme.typography.fontFamily,
                },
                "&:before": { display: "none" },
                "&.Mui-expanded": { margin: 0 },
                "&:hover": {
                    borderColor: highlighted ? theme.palette.warning.main : alpha(accentColor, 0.34),
                    boxShadow:
                        highlighted ?
                            `0 0 0 3px ${alpha(theme.palette.warning.main, 0.12)}`
                        :   `0 8px 20px ${alpha(theme.palette.common.black, 0.04)}`,
                },
            }}
        >
            <AccordionSummary
                component="div"
                expandIcon={
                    <IconButton size="small">
                        <ExpandMoreIcon fontSize="small" />
                    </IconButton>
                }
                sx={{
                    minHeight: 72,
                    px: theme.spacing(2.5),
                    py: theme.spacing(1),
                    "& .MuiAccordionSummary-content": {
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "44px 44px minmax(0, 1fr)",
                            xl:
                                item.required ?
                                    "44px 44px minmax(260px, 1fr) minmax(320px, max-content)"
                                :   "44px 44px minmax(260px, 1fr) minmax(320px, max-content) max-content",
                        },
                        alignItems: "center",
                        gap: theme.spacing(2),
                        my: theme.spacing(1),
                        overflow: "hidden",
                    },
                }}
            >
                <Checkbox
                    checked={selected}
                    inputProps={{
                        "aria-label": t("settings.consent.selection.select_item", {
                            defaultValue: "Select {{name}}",
                            name: item.name,
                        }),
                    }}
                    onChange={(event) => onSelect(item.id, event.target.checked)}
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                        flexShrink: 0,
                        height: 44,
                        width: 44,
                        "&:focus-visible": {
                            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}`,
                        },
                    }}
                />

                <Box
                    sx={{
                        width: 44,
                        height: 44,
                        borderRadius: theme.spacing(1.5),
                        bgcolor: alpha(itemIconColor, 0.08),
                        color: itemIconColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    <FoodsIcon icon={itemIcon} size={2} />
                </Box>

                <Stack sx={{ gridColumn: { xs: "3 / -1", xl: "auto" }, minWidth: 0, gap: "2px" }}>
                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography
                            variant="body-bold"
                            sx={{
                                maxWidth: "100%",
                                overflow: "hidden",
                                overflowWrap: "anywhere",
                                textOverflow: "ellipsis",
                                whiteSpace: { xs: "normal", md: "nowrap" },
                            }}
                        >
                            {item.name}
                        </Typography>

                        {categoryLabel && <FoodsTags label={categoryLabel} variant={categoryTagVariant} />}
                    </Stack>
                </Stack>

                {latestVersion && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        gap={1}
                        sx={{
                            flexWrap: "wrap",
                            gridColumn: { xs: "1 / -1", md: "3 / -1", xl: "auto" },
                            minWidth: 0,
                            maxWidth: "100%",
                        }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <FoodsTags label={formatConsentVersionBadgeLabel(latestVersion.version)} variant="tags-grey" />
                        {latestVersionTitle && (
                            <Typography
                                variant="small-bold"
                                sx={{
                                    borderLeft: `1px solid ${theme.custom.grey[500]}`,
                                    color: theme.custom.grey[2800],
                                    maxWidth: { xs: "100%", lg: 220 },
                                    overflow: "hidden",
                                    pl: 1,
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {latestVersionTitle}
                            </Typography>
                        )}
                        {latestVersionDateLabel && (
                            <Typography
                                variant="small-semibold"
                                sx={{
                                    borderLeft: `1px solid ${theme.custom.grey[500]}`,
                                    color: theme.custom.grey[2000],
                                    pl: 1,
                                    whiteSpace: { xs: "normal", md: "nowrap" },
                                }}
                            >
                                {latestVersionDateLabel}
                            </Typography>
                        )}
                    </Stack>
                )}

                {!item.required && (
                    <Box
                        onClick={(event) => event.stopPropagation()}
                        sx={{ gridColumn: { xs: "1 / -1", md: "3 / -1", xl: "auto" }, minWidth: 0 }}
                    >
                        <Stack direction="row" alignItems="center" gap={0.75}>
                            <ConsentControlledSwitch
                                checked={item.active}
                                disabled={isPatchPending}
                                inputProps={{
                                    "aria-label": t("settings.consent.item.toggle_active", {
                                        defaultValue: "Toggle visibility for {{name}}",
                                        name: item.name,
                                    }),
                                }}
                                onChange={(active) => onToggleActive(item, active)}
                                sx={{ m: 0 }}
                            />
                            <Typography
                                variant="small-semibold"
                                sx={{
                                    color: item.active ? theme.palette.success.main : theme.custom.grey[2000],
                                }}
                            >
                                {t(availabilityDescriptor.key, availabilityDescriptor.fallback)}
                            </Typography>
                        </Stack>
                    </Box>
                )}
            </AccordionSummary>

            <AccordionDetails sx={{ px: theme.spacing(2.5), pb: theme.spacing(2.5), pt: 0 }}>
                <Box
                    sx={{
                        borderTop: `1px solid ${theme.custom.grey[500]}`,
                        pt: theme.spacing(2),
                    }}
                >
                    <Stack gap={theme.spacing(2)}>
                        <Stack direction={{ xs: "column", md: "row" }} gap={theme.spacing(3)}>
                            <Box>
                                <Typography
                                    variant="small-semibold"
                                    sx={{
                                        color: theme.custom.grey[2000],
                                        display: "block",
                                        mb: 0.5,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    {t("settings.consent.item.code", "Code")}
                                </Typography>
                                <Box
                                    component="code"
                                    sx={{
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                        color: theme.palette.primary.main,
                                        bgcolor: theme.palette.primary[50],
                                        px: "10px",
                                        py: "5px",
                                        borderRadius: theme.spacing(0.5),
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        display: "inline-block",
                                    }}
                                >
                                    {item.code}
                                </Box>
                            </Box>
                            {categoryLabel && (
                                <Box>
                                    <Typography
                                        variant="small-semibold"
                                        sx={{
                                            color: theme.custom.grey[2000],
                                            display: "block",
                                            mb: 0.5,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        {t("settings.consent.item.category", "Category")}
                                    </Typography>
                                    <FoodsTags label={categoryLabel} variant={categoryTagVariant} />
                                </Box>
                            )}
                        </Stack>
                        <Stack direction="row" gap={theme.spacing(1)} flexWrap="wrap">
                            <ConsentActionIconButton
                                icon="edit"
                                label={editLabel}
                                onClick={() => onEdit(item)}
                                color={theme.palette.primary.main}
                            />
                            <ConsentActionIconButton
                                icon="viewPwd"
                                label={previewLabel}
                                disabled={!latestVersion}
                                disabledTooltip={previewDisabledLabel}
                                onClick={() => onPreview(item, latestVersion)}
                                color={theme.palette.info.main}
                            />
                            <ConsentActionIconButton
                                icon="delete"
                                label={deleteLabel}
                                onClick={() => onDelete(item)}
                                color={theme.palette.error.main}
                            />
                        </Stack>
                    </Stack>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
